import React, { useCallback, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getOverview, getCreceBreakdown } from '../api';
import KPICard from '../components/KPICard';
import { formatNumber } from '../utils/format';
import AnalyticsFilterBar from '../components/AnalyticsFilterBar';
import DownloadButton from '../components/DownloadButton';
import CalificacionDelEquipo from './overview/CalificacionDelEquipo';
import { usePeticionVigente } from '../utils/peticionVigente';

const SENTIMENT_COLORS = { positive: '#22c55e', neutral: '#6b7280', negative: '#ef4444' };

export default function Overview() {
  const [data, setData] = useState(null);
  const [crece, setCrece] = useState([]);
  const [loading, setLoading] = useState(true);
  // null hasta que AnalyticsFilterBar emite los filtros (incl. la semana por
  // defecto). Así la primera consulta ya viene acotada y no traemos TODO.
  const [filters, setFilters] = useState(null);
  const nuevaPeticion = usePeticionVigente();

  const fetchAll = useCallback((params) => {
    const vigente = nuevaPeticion();
    setLoading(true);
    Promise.all([
      getOverview(params).then(r => { if (vigente()) setData(r.data); }),
      getCreceBreakdown(params)
        .then(r => { if (vigente()) setCrece(r.data.classifications || []); })
        .catch(() => { if (vigente()) setCrece([]); }),
    ]).finally(() => { if (vigente()) setLoading(false); });
  }, [nuevaPeticion]);

  useEffect(() => { if (filters) fetchAll(filters); }, [filters, fetchAll]);

  // La barra se renderiza SIEMPRE en la misma posición (fuera del condicional de
  // carga) para que no se remonte al alternar loading — si se remontara, perdería
  // el estado de los filtros y re-emitiría onChange, provocando un loop de carga.
  const sentimentData = data ? Object.entries(data.sentiment_distribution).map(([name, value]) => ({ name, value })) : [];
  const languageData = data ? Object.entries(data.language_distribution).map(([name, value]) => ({ name, value })) : [];
  const creceTotal = crece.reduce((s, c) => s + c.count, 0);
  const creceRows = crece.map(c => ({
    name: c.name,
    count: c.count,
    pct: creceTotal ? ((c.count / creceTotal) * 100).toFixed(1) : '0',
  }));

  return (
    <div>
      <h1 className="page-title">Overview</h1>
      <p className="page-subtitle">Dashboard general de llamadas del call center</p>
      {/* Arriba y ANTES de la barra de filtros: lleva su propio periodo (los
          30 días del Inicio de cada ejecutivo) y no se mueve con ella. */}
      <CalificacionDelEquipo />
      <AnalyticsFilterBar onChange={setFilters} />

      {loading ? (
        <div className="loading">Cargando dashboard...</div>
      ) : !data ? (
        <div className="loading">No se pudo cargar el dashboard</div>
      ) : (
        <>
          <div className="kpi-row">
            <KPICard value={formatNumber(data.total_calls)} label="Total llamadas" />
            <KPICard value={formatNumber(data.total_processed)} label="Procesadas" />
            <KPICard value={`${Math.round(data.avg_duration_seconds)}s`} label="Duracion promedio" />
            <KPICard value={`${data.total_hours_audio.toFixed(0)}h`} label="Horas de audio" />
            <KPICard value={formatNumber(data.total_pending)} label="Pendientes" />
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div className="card-title">Atención y transferencias (CRECE)</div>
              <DownloadButton
                columns={[
                  { key: 'name', label: 'Tipo de atención' },
                  { key: 'count', label: 'Llamadas' },
                  { key: 'pct', label: '%' },
                ]}
                rows={creceRows}
                filename="crece-clasificacion"
              />
            </div>
            {crece.length === 0 ? (
              <p style={{ color: '#6b7280', margin: 0 }}>Sin datos para el filtro seleccionado.</p>
            ) : (
              crece.map((c) => {
                const pct = creceTotal ? (c.count / creceTotal) * 100 : 0;
                return (
                  <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '5px 0' }}>
                    <div style={{ width: 210, fontSize: 13, color: '#cbd5e1' }}>{c.name}</div>
                    <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 4, height: 16 }}>
                      <div style={{ width: `${pct}%`, background: '#818cf8', height: '100%', borderRadius: 4 }} />
                    </div>
                    <div style={{ width: 140, textAlign: 'right', fontSize: 13 }}>
                      {formatNumber(c.count)} <span style={{ color: '#6b7280' }}>({pct.toFixed(1)}%)</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="charts-grid">
            <div className="card">
              <div className="card-title">Llamadas por dia</div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.calls_by_day}>
                  <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#1a1a24', border: '1px solid #333', borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#818cf8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <div className="card-title">Distribucion de sentimiento</div>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={sentimentData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {sentimentData.map((entry) => (
                      <Cell key={entry.name} fill={SENTIMENT_COLORS[entry.name] || '#818cf8'} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1a1a24', border: '1px solid #333', borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <div className="card-title">Top temas detectados</div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.top_topics.slice(0, 10)} layout="vertical">
                  <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <YAxis type="category" dataKey="topic" tick={{ fill: '#9ca3af', fontSize: 11 }} width={150} />
                  <Tooltip contentStyle={{ background: '#1a1a24', border: '1px solid #333', borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#a78bfa" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <div className="card-title">Top intenciones</div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.top_intents.slice(0, 10)} layout="vertical">
                  <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <YAxis type="category" dataKey="intent" tick={{ fill: '#9ca3af', fontSize: 11 }} width={150} />
                  <Tooltip contentStyle={{ background: '#1a1a24', border: '1px solid #333', borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#6ee7b7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {languageData.length > 0 && (
            <div className="card" style={{ maxWidth: 400 }}>
              <div className="card-title">Idiomas detectados</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '8px 0' }}>
                {languageData.map(l => (
                  <div key={l.name} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: '#a5b4fc' }}>{formatNumber(l.value)}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{l.name.toUpperCase()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
